import { useTranslation, Markdown, lang } from '@/helpers/translate';
import { getPathByLang } from '@/helpers/router.js';
import { useZusUserConnectedStore } from '@/helpers/user-auth.js';
import { useSearchZustandStore } from '@/helpers/searchctx.js';
import HofPlusIcon from '@/components/icons/hofplus';

/**
 * WEA-157 — Bandeau d'explication Legend+ (zone des résultats de la search).
 *
 * Affiché UNIQUEMENT sous le filtre "Legend +" (hofFilter === 'legendplus'). Texte selon le profil
 * du viewer (masqué pour un abonné actif) + un lien "en savoir plus" vers la page Hall Of Fame
 * (nouvel onglet). Il n'y a plus de cases d'options : le filtrage passe par les boutons de la barre.
 * Couleur : rouge WAS en teinte reduite (bg-red/10), le violet reste celui des paliers HOF.
 */
export default function HofBanner() {
  const { t } = useTranslation();
  const langCurrent = lang();
  const hofFilter = useSearchZustandStore((state) => state.hofFilter);
  const isAuthenticated = useZusUserConnectedStore((state) => state.isAuthenticated);
  const userData = useZusUserConnectedStore((state) => state.userData);

  if (hofFilter !== 'legendplus') return null;

  const corpo = userData?.corpo;
  const isSubscriber = !!isAuthenticated
    && ['active', 'trialing'].includes(corpo?.subscription?.status);
  const earnedLevel = [5, 10, 15].includes(corpo?.fidelityLevel) ? corpo.fidelityLevel : null;
  let profile = 'guest';
  if (isSubscriber) profile = 'subscriber';
  else if (earnedLevel === 15) profile = 'legend';
  else if (earnedLevel) profile = 'member';
  else if (isAuthenticated) profile = 'nonMember';

  const bannerKeyByProfile = {
    guest: 'search:hofDiscount.bannerGuest',
    nonMember: 'search:hofDiscount.bannerProspect',
    member: 'search:hofDiscount.bannerProspect',
    legend: 'search:hofDiscount.bannerLegend',
    // Abonné actif : rien à lui expliquer -> pas de bandeau.
  };
  const key = bannerKeyByProfile[profile];
  if (!key) return null;

  const hofUrl = getPathByLang('hall-of-fame', langCurrent);

  return (
    <div className="px-6 pt-24 lg:px-8 lg:pt-6">
      {/* Meme composition que le teaser du header et l'opt-in du cartridge : fond bg-red/10 sans
          bordure, etoile seule sur pastille red/15. Les trads portent un `text-purple` inline sur les
          pourcentages : on le ramene au rouge ici pour garder une seule couleur d'accent. */}
      <div className="flex gap-4 items-center px-4 py-4 rounded-xl bg-red/10">
        <span className="flex justify-center items-center rounded-lg size-11 shrink-0 bg-red/15 text-red">
          <HofPlusIcon className="size-7" />
        </span>
        <div className="min-w-0">
          <p className="text-[15px] leading-snug text-was-ink [&_.text-purple]:text-red [&_strong]:text-red">
            <Markdown>{t(key)}</Markdown>
          </p>
          <a
            href={hofUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex gap-1 items-center mt-1.5 text-sm font-medium text-red hover:text-red-dark"
          >
            {t('search:hofDiscount.learnMore')}
            <span aria-hidden="true">→</span>
          </a>
        </div>
      </div>
    </div>
  );
}
